// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  getUniqueViolationColumnId,
  handleAPIError,
  hasSkuExistsError,
  hasUniqueViolationError,
} from '@/api/error-utils';
import {
  AutoAwesomeOutlined,
  CloseRounded,
} from '@/constants/hardfin-standard-icons';
import { useToast } from '@/contexts/toast-context';
import { classifyTriageItem } from '@/items/api/triage-classify-item';
import { ClassificationType, ItemTriage } from '@/items/api/triage-types';
import { ItemSearch } from '@/items/components/item-search';
import { ItemTypePicker } from '@/items/components/item-type-picker';
import { TriageExternalSystemIcon } from '@/items/components/triage-external-system-icon';
import {
  ITEM_TYPE_TO_CLASSIFICATION,
  type ItemType,
} from '@/items/constants/item-types';
import { itemTriageQueryKey } from '@/items/triage-query-keys';
import { generateSkuSuggestion } from '@/items/utilities/generate-sku-suggestion';
import { SparseItem } from '@/sparse/api/types';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Link as MuiLink,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';

interface TriageClassifySkuModalProps {
  isOpen: boolean;
  item: ItemTriage | null;
  onClose: () => void;
  onSuccess?: (item: ItemTriage) => void;
}

/**
 * Modal dialog for classifying a triage item.
 * Displays the item's SKU and name, and provides three classification options.
 */
export function TriageClassifySkuModal(props: TriageClassifySkuModalProps) {
  const { isOpen, item, onClose, onSuccess } = props;
  const [selectedItemType, setSelectedItemType] = useState<ItemType | null>(
    null
  );
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [editedName, setEditedName] = useState('');
  const [editedSku, setEditedSku] = useState('');
  const [nameError, setNameError] = useState('');
  const [skuError, setSkuError] = useState('');
  const [matchedItem, setMatchedItem] = useState<SparseItem | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  useEffect(() => {
    if (isOpen && item) {
      setEditedName(item.itemName);
      setEditedSku(item.sku ?? '');
      setNameError('');
      setSkuError('');
      setMatchedItem(null);
      setShowCreateForm(false);
    }
  }, [isOpen, item]);

  const skuSuggestion = useMemo(() => {
    if (!editedName || editedName.trim() === '') {
      return null;
    }
    return generateSkuSuggestion(editedName);
  }, [editedName]);

  const showSkuSuggestion = skuSuggestion !== null && editedSku === '';

  const handleAcceptSkuSuggestion = (e: React.MouseEvent) => {
    e.preventDefault();
    if (skuSuggestion) {
      setEditedSku(skuSuggestion.sku);
      setSkuError('');
    }
  };

  const isMatchMode = matchedItem !== null;

  const classifyMutation = useMutation({
    mutationFn: () => {
      if (!item) {
        throw new Error('No item selected');
      }

      if (matchedItem) {
        return classifyTriageItem(item.key, {
          existingItemId: matchedItem.id,
        });
      }

      if (!selectedItemType) {
        throw new Error('No classification selected');
      }
      // Convert ItemType to ClassificationType for the API
      const classification = ITEM_TYPE_TO_CLASSIFICATION[
        selectedItemType
      ] as ClassificationType;
      return classifyTriageItem(item.key, {
        classification,
        name: editedName.trim(),
        sku: editedSku.trim(),
      });
    },
    onSuccess: (updatedItem) => {
      queryClient.invalidateQueries({ queryKey: itemTriageQueryKey });
      setErrorMessage('');
      setSelectedItemType(null);
      setMatchedItem(null);
      setShowCreateForm(false);
      onClose();
      onSuccess?.(updatedItem);

      if (updatedItem.item) {
        const label = updatedItem.item.sku
          ? `${updatedItem.item.sku} (${updatedItem.item.name})`
          : updatedItem.item.name;
        showToast({
          severity: 'success',
          duration: 5000,
          element: (
            <span>
              View{' '}
              <Link
                to="/catalog/items/$itemKey"
                params={{ itemKey: updatedItem.item.key }}
              >
                <MuiLink
                  component="span"
                  sx={{
                    fontWeight: 600,
                    color: 'primary.main',
                    cursor: 'pointer',
                  }}
                >
                  {label}
                </MuiLink>
              </Link>
            </span>
          ),
        });
      }
    },
    onError: (error) => {
      const apiError = handleAPIError(error);
      if (hasUniqueViolationError(error)) {
        const columnId = getUniqueViolationColumnId(error);
        if (columnId === 'name') {
          setNameError('An item with this name already exists');
        } else {
          setSkuError('An item with this SKU already exists');
        }
      } else if (hasSkuExistsError(error)) {
        setSkuError('An item with this SKU already exists');
      } else {
        setErrorMessage(apiError.message || 'Failed to classify item');
      }
    },
  });

  const handleConfirm = () => {
    setErrorMessage('');
    setNameError('');
    setSkuError('');

    if (isMatchMode) {
      classifyMutation.mutate();
      return;
    }

    if (!editedName.trim()) {
      setNameError('Name is required');
      return;
    }
    if (!editedSku.trim()) {
      setSkuError('SKU is required');
      return;
    }

    classifyMutation.mutate();
  };

  const handleClose = () => {
    if (classifyMutation.isPending) {
      return;
    }
    setErrorMessage('');
    setSelectedItemType(null);
    setEditedName('');
    setEditedSku('');
    setNameError('');
    setSkuError('');
    setMatchedItem(null);
    setShowCreateForm(false);
    onClose();
  };

  const handleDialogClose = (_event: object, reason: string) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      if (classifyMutation.isPending) {
        return;
      }
    }
    handleClose();
  };

  if (!item) {
    return null;
  }

  return (
    <Dialog
      open={isOpen}
      onClose={handleDialogClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="classify-sku-dialog-title"
    >
      <DialogTitle
        id="classify-sku-dialog-title"
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          pb: 0,
        }}
      >
        <Box>
          <Typography variant="h6" component="span">
            Classify SKU
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Determine how this SKU should be tracked in Hardfin
          </Typography>
        </Box>
        <IconButton
          size="small"
          onClick={handleClose}
          disabled={classifyMutation.isPending}
          aria-label="Close"
        >
          <CloseRounded />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ pt: 2 }}>
          {/* Match to existing item — hidden when create form is open */}
          {!showCreateForm && (
            <>
              <Box>
                <Typography variant="subtitle1" gutterBottom mb={1}>
                  Match to existing item
                </Typography>
                <ItemSearch
                  value={matchedItem}
                  onChange={(value) => {
                    setMatchedItem(value);
                  }}
                  excludeLinkedIntegration={item.externalSystem.id}
                  fullWidth
                  placeholderText="Search items..."
                  showSearchAdornment
                />
              </Box>

              <Divider />

              <Button
                variant="outlined"
                onClick={() => {
                  setShowCreateForm(true);
                  setMatchedItem(null);
                }}
                disabled={classifyMutation.isPending}
              >
                Create new item for SKU
              </Button>
            </>
          )}

          {/* Create new item — matches the original classify form layout */}
          {showCreateForm && (
            <Stack spacing={3}>
              {/* Item info card */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  p: 2,
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                }}
              >
                <TriageExternalSystemIcon
                  system={item.externalSystem}
                  size="large"
                />
                <Stack direction="column" spacing={1.5} sx={{ flex: 1 }}>
                  <TextField
                    label="Name"
                    size="small"
                    variant="outlined"
                    value={editedName}
                    onChange={(e) => {
                      setEditedName(e.target.value);
                      setNameError('');
                    }}
                    error={Boolean(nameError)}
                    helperText={nameError}
                  />
                  <Stack spacing={0.5}>
                    <TextField
                      label="SKU"
                      size="small"
                      variant="outlined"
                      value={editedSku}
                      onChange={(e) => {
                        setEditedSku(e.target.value);
                        setSkuError('');
                      }}
                      error={Boolean(skuError)}
                      helperText={skuError}
                    />
                    {showSkuSuggestion && (
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={0.5}
                        px={2}
                      >
                        <AutoAwesomeOutlined
                          fontSize="small"
                          color="action"
                          sx={{ fontSize: 16 }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          Suggested by Hardfin AI:
                        </Typography>
                        <MuiLink
                          href="#"
                          onClick={handleAcceptSkuSuggestion}
                          variant="caption"
                          sx={{ '&:hover': { cursor: 'pointer' } }}
                        >
                          {skuSuggestion?.sku}
                        </MuiLink>
                      </Stack>
                    )}
                  </Stack>
                </Stack>
              </Box>

              {/* Classification type selection */}
              <Box>
                <Typography variant="subtitle1" gutterBottom mb={1}>
                  Select classification type
                </Typography>
                <ItemTypePicker
                  selectedType={selectedItemType}
                  onSelect={setSelectedItemType}
                />
              </Box>
            </Stack>
          )}

          {/* Error message */}
          {errorMessage && (
            <Alert severity="error" onClose={() => setErrorMessage('')}>
              {errorMessage}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Box>
          {showCreateForm && (
            <Button
              variant="text"
              onClick={() => {
                setShowCreateForm(false);
                setSelectedItemType(null);
              }}
              disabled={classifyMutation.isPending}
            >
              Back
            </Button>
          )}
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            variant="text"
            onClick={handleClose}
            disabled={classifyMutation.isPending}
          >
            Cancel
          </Button>
          <Tooltip
            title={
              !isMatchMode && showCreateForm && !selectedItemType
                ? 'Select a classification type to continue'
                : !isMatchMode && !showCreateForm
                  ? 'Select an existing item or create a new one'
                  : ''
            }
            arrow
          >
            <span>
              <Button
                variant="contained"
                onClick={handleConfirm}
                disabled={
                  classifyMutation.isPending ||
                  (!isMatchMode && (!showCreateForm || !selectedItemType))
                }
              >
                {classifyMutation.isPending
                  ? isMatchMode
                    ? 'Linking...'
                    : 'Classifying...'
                  : isMatchMode
                    ? 'Match to existing item'
                    : 'Confirm classification and import SKU'}
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
